// src/components/admin/AdminCondosListPage.tsx
// Admin page to list all condos (published or draft) in a grid with Edit buttons

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { condoService } from '../../services/condoService';
import { Condo } from '../../types/condo';
import BackgroundShapes from '../common/BackgroundShapes';
import AdminCondoCard from './AdminCondoCard';

interface AdminCondosListPageProps {
  isDrafts?: boolean;
}

const AdminCondosListPage: React.FC<AdminCondosListPageProps> = ({ isDrafts = false }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [condos, setCondos] = useState<Condo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCondos = async () => {
      try {
        setLoading(true);
        setError(null);
        if (isDrafts) {
          const res = await condoService.listDraftCondos();
          setCondos(res.data?.condos ?? []);
        } else {
          const res = await condoService.listCondos();
          setCondos(res.data?.condos ?? []);
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load condos';
        setError(message);
        setCondos([]);
      } finally {
        setLoading(false);
      }
    };
    loadCondos();
  }, [isDrafts]);

  const handleBackToDashboard = () => {
    navigate('/admin/dashboard');
  };

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const pageTitle = isDrafts ? 'Incomplete Condos' : 'Published Condos';
  const pageSubtitle = isDrafts ? 'Resume editing draft condo listings' : 'View and edit condo listings';
  const emptyMessage = isDrafts ? 'No draft condos' : 'No condos yet';
  const emptySubMessage = isDrafts
    ? 'Drafts will appear here when you save a condo without publishing.'
    : 'Create your first condo from the dashboard.';

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBackToDashboard}
              className="bg-cream border-2 border-coffee text-coffee px-3 py-2 rounded-lg hover:bg-white transition-colors shadow-sm text-sm font-medium"
            >
              ← Dashboard
            </button>
            <div>
              <h1 className="text-3xl font-bold text-coffee mb-1">
                {pageTitle}
              </h1>
              <p className="text-coffee opacity-80 text-sm">
                {pageSubtitle}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors shadow-sm"
          >
            Sign Out
          </button>
        </div>

        {/* Content */}
        {loading && (
          <div className="text-center py-12 text-coffee">
            Loading condos...
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6 text-red-800">
            {error}
          </div>
        )}

        {!loading && !error && condos.length === 0 && (
          <div className="text-center py-12">
            <p className="text-coffee text-xl mb-2">{emptyMessage}</p>
            <p className="text-coffee opacity-80 mb-4">{emptySubMessage}</p>
            <button
              onClick={handleBackToDashboard}
              className="bg-terracotta text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm"
            >
              Go to Dashboard
            </button>
          </div>
        )}

        {!loading && !error && condos.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...condos].sort((a, b) => {
              const PRIORITY: Record<string, number> = { user: 1, admin: 2, scraper: 3, backend: 4 };
              const src = (c: Condo) => c.createdVia ?? (c.sourceUrl ? 'scraper' : c.submittedBy ? 'user' : 'admin');
              return (PRIORITY[src(a)] ?? 2) - (PRIORITY[src(b)] ?? 2);
            }).map((condo) => (
              <AdminCondoCard
                key={condo.id ?? condo._id ?? condo.name}
                condo={condo}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCondosListPage;
